// src/components/PagoPensionWidget.js
//
// Widget de pago Wompi para una cuota de pensión — mismo patrón que
// PagoInscripcionWidget.js de fenagro_front (que ya está en producción
// para el pago de inscripción de Fenagro). Misma cuenta comercial Wompi,
// mismo router de webhooks -- ver PagoPensionController en el backend.

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Paper, Divider } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import { pensionService } from '../services/api';

export default function PagoPensionWidget({ cuota }) {
  const wompiRef = useRef(null);
  const [fase, setFase] = useState('idle'); // idle | iniciando | widget
  const [wompiData, setWompiData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!wompiData) return;
    const timer = setTimeout(() => {
      const c = wompiRef.current;
      if (!c) return;

      const key = wompiData.publicKey || '';
      if (!key || key.length < 10) {
        setError('La pasarela de pagos no está configurada. Contacta al administrador de tu institución.');
        setFase('idle');
        return;
      }

      c.innerHTML = '';
      const script = document.createElement('script');
      script.src = 'https://checkout.wompi.co/widget.js';
      script.setAttribute('data-render', 'button');
      script.setAttribute('data-public-key', key);
      script.setAttribute('data-currency', wompiData.currency || 'COP');
      script.setAttribute('data-amount-in-cents', String(wompiData.amountInCents));
      script.setAttribute('data-reference', wompiData.referencia);
      script.setAttribute('data-signature:integrity', wompiData.integrityHash);
      script.setAttribute('data-redirect-url', wompiData.redirectUrl);
      script.onload = () => setFase('widget');
      script.onerror = () => {
        setError('No se pudo conectar con Wompi. Intenta de nuevo.');
        setFase('idle');
      };
      c.appendChild(script);
    }, 100);

    return () => clearTimeout(timer);
  }, [wompiData]);

  const iniciar = async () => {
    setFase('iniciando');
    setError('');
    try {
      const data = await pensionService.iniciarPagoWompi(cuota.id);
      setWompiData(data);
    } catch (e) {
      setError(e?.message || 'Error al iniciar el pago. Intenta de nuevo.');
      setFase('idle');
    }
  };

  const saldo = cuota.balance ?? cuota.finalValue;

  // Si la cuota ya está pagada (puede pasar si el usuario vuelve después
  // de pagar y el webhook ya la confirmó), no mostrar el botón de pago.
  // Este chequeo va DESPUÉS de los hooks -- nunca antes, para no violar
  // las reglas de hooks (siempre deben correr en el mismo orden).
  if (cuota.status === 'Pagada') {
    return (
      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 2 }}>
        <strong>Cuota pagada.</strong>
      </Alert>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '2px solid #2e7d32', borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <PaymentIcon sx={{ color: '#fff', fontSize: 28 }} />
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
            {cuota.description || `Cuota ${cuota.number}`}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
            Vence: {cuota.dueDate ? new Date(cuota.dueDate).toLocaleDateString() : '—'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
            <Typography variant="body2" color="text.secondary">Valor de la cuota</Typography>
            <Typography variant="body2" fontWeight={600}>${Number(cuota.finalValue).toLocaleString('es-CO')} COP</Typography>
          </Box>
          {cuota.totalPaid > 0 && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body2" color="text.secondary">Ya pagado</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  -${Number(cuota.totalPaid).toLocaleString('es-CO')} COP
                </Typography>
              </Box>
            </>
          )}
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, background: '#f0fdf0', borderRadius: 1, px: 1, mt: 1 }}>
            <Typography variant="body2" fontWeight={700}>Saldo pendiente</Typography>
            <Typography variant="h6" fontWeight={800} color="#1a4d2e">
              ${Number(saldo).toLocaleString('es-CO')} COP
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {fase === 'idle' && (
          <Button fullWidth variant="contained" size="large" onClick={iniciar} startIcon={<PaymentIcon />}
            sx={{ background: 'linear-gradient(135deg,#1a4d2e,#2e7d32)', fontWeight: 700, borderRadius: 2, py: 1.5 }}>
            Pagar ${Number(saldo).toLocaleString('es-CO')} COP con Wompi
          </Button>
        )}

        {fase === 'iniciando' && !wompiData && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} sx={{ color: '#2e7d32' }} />
            <Typography variant="body2" color="text.secondary">Preparando pasarela de pago…</Typography>
          </Box>
        )}

        {wompiData && (
          <Box>
            {fase === 'iniciando' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Cargando pasarela…</Typography>
              </Box>
            )}
            <Box ref={wompiRef} />
            {fase === 'widget' && (
              <Button size="small" color="inherit" sx={{ mt: 1, color: '#aaa', fontSize: '0.72rem' }}
                onClick={() => { setWompiData(null); setFase('idle'); }}>
                Cancelar
              </Button>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 2 }}>
          <LockIcon sx={{ fontSize: 14, color: '#aaa' }} />
          <Typography variant="caption" color="text.disabled">
            Pago seguro procesado por Wompi · Tus datos están protegidos
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
